import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { addDays, format } from 'date-fns';
import getDate from 'date-fns/getDate';
import parseISO from 'date-fns/parseISO';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import {
  paymentMethod,
  paymentType,
  originalGSNICStates,
  soldToEffectiveDaysIntervalByState,
  policyType as PolicyTypes
} from '@ourbranch/lookups';

import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { FormField } from 'core/components/form';
import withDatePicker from 'core/components/with-date-picker';
import { MegaDownPayCard } from './mega-down-pay-card';
import { canOfferMegaDownPay, isMegaDownPayRequired } from 'offer/helpers/mega-down-pay-helpers';
import { OfferPageTabs } from 'core/store/offer-store';
import { UpdateMortgageNotification } from 'common/components/mortgage-details';
import { FutureEffectiveDateWarning } from './future-effective-date-warning';
import useStyles from './settings.styles';

const CondoSettings = observer(({ paymentMethodOptions, billingDaysOptions, paymentTypeOptions }) => {
  const { offer } = useStore();
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { isBix } = offer;
  const { primaryMortgageDetail } = values;
  const [isPaymentMortgage, setIsPaymentMortgage] = useState(values.global.condoPaymentMethod === paymentMethod.Escrow);
  const state = offer.state;
  const daysOut = soldToEffectiveDaysIntervalByState(state, PolicyTypes.Condo);
  const showCreditCardNote =
    values.global.condoPaymentMethod === paymentMethod.ACH && !isBix && originalGSNICStates.includes(state);

  const offerMegaDownPay = useMemo(
    () =>
      canOfferMegaDownPay(
        values.global.condoEffectiveDate,
        values.global.condoBillingDayOfMonth,
        values.global.condoPaymentType
      ),
    [values.global.condoEffectiveDate, values.global.condoBillingDayOfMonth, values.global.condoPaymentType]
  );

  const megaDownPayRequired = useMemo(
    () =>
      isMegaDownPayRequired(
        values.global.condoEffectiveDate,
        values.global.condoBillingDayOfMonth,
        values.global.condoPaymentType
      ),
    [values.global.condoEffectiveDate, values.global.condoBillingDayOfMonth, values.global.condoPaymentType]
  );

  const goToCondoDetailsTab = () => {
    offer.setCurrentTab(OfferPageTabs.CONDO);
    offer.setScrollTo('addMortgage');
  };

  useEffect(() => {
    setIsPaymentMortgage(values.global.condoPaymentMethod === paymentMethod.Escrow);
  }, [values.global.condoPaymentMethod]);

  // this is used to default the billing day of the month to the day that the policy is effective
  const condoEffectiveDay = useMemo(() => {
    if (values.global.condoEffectiveDate) {
      return getDate(parseISO(values.global.condoEffectiveDate));
    }
  }, [values.global.condoEffectiveDate]);

  // if condo billing day of month is null, default the selection to the effective day of the policy
  // or the 28th if the effectiveDay is past the allowed options of days 1 - 28
  useEffect(() => {
    if (!values.global.condoBillingDayOfMonth && values.global.condoBillingDayOfMonth !== condoEffectiveDay) {
      if (condoEffectiveDay > 28) {
        setFieldValue('global.condoBillingDayOfMonth', 28);
      } else {
        setFieldValue('global.condoBillingDayOfMonth', condoEffectiveDay);
      }
    }
  }, [values, condoEffectiveDay, setFieldValue]);

  const onCondoPaymentChange = useCallback(
    (condoPaymentMethod) => {
      if (condoPaymentMethod === paymentMethod.Escrow) {
        // set payment freq to Mortgage
        setFieldValue('global.condoPaymentType', paymentType.Escrow);
      } else {
        setFieldValue('global.condoPaymentType', paymentType.OneTime);
      }
    },
    [setFieldValue]
  );

  const onEffectiveDateChange = (val) => {
    if (['H', 'C', 'HA', 'CA'].includes(values.selectedOption)) {
      // If home/condo purchase date matches policy effective date, make sure they keep matching when effective date changes
      const policyType = values.selectedOption.includes('H') ? 'home' : 'condo';
      const propertyPurchaseDate = values[policyType]?.purchaseDate || values[policyType]?.purchaseDate;
      const policyEffectiveDate =
        values.global[`${policyType}EffectiveDate`] || values.global[`${policyType}EffectiveDate`];

      if (propertyPurchaseDate === policyEffectiveDate) {
        setFieldValue(`${policyType}.purchaseDate`, format(new Date(val.stringValue), 'yyyy-MM-dd'));
      }
    }
  };

  return (
    <Section title="Condo Policy Settings" type="SubSection">
      <Card className={classes.container} type="secondary" id="condoSettings">
        <Label className={classes.subtitle} type="formSubTitle">
          Policy Term
        </Label>
        <Grid container alignItems="center">
          <FormField
            type="date"
            id="global.condoEffectiveDate"
            name="global.condoEffectiveDate"
            label="Condo policy effective date"
            xs={4}
            mode="dark"
            disableFuture={false}
            minDate={addDays(new Date(), daysOut)}
            maxDate={addDays(new Date(), 60)}
            fast={false}
            onChange={onEffectiveDateChange}
          />
          <Grid item xs={12}>
            <FutureEffectiveDateWarning daysOut={daysOut} />
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.billingDetails} alignItems="center">
          <Grid item xs={2}>
            <Label type="formSubTitle" className={classes.subtitle}>
              Billing Details
            </Label>
          </Grid>
          {showCreditCardNote && (
            <Grid item xs={6}>
              <Label type="noteSmall">Note: There is no additional fee for paying with credit card.</Label>
            </Grid>
          )}
        </Grid>

        <Grid container>
          <Grid container spacing={4}>
            <FormField
              label="Condo payment method"
              xs={4}
              type="select"
              name="global.condoPaymentMethod"
              options={paymentMethodOptions}
              onChange={onCondoPaymentChange}
              mode="dark"
            />
            <FormField
              label="Condo payment frequency"
              xs={4}
              type="select"
              name="global.condoPaymentType"
              options={paymentTypeOptions.filter((option) => {
                if (!isPaymentMortgage) {
                  return option.id !== paymentType.Escrow;
                }
                return option;
              })}
              mode="dark"
              disabled={isPaymentMortgage}
              fast={false}
            />
            {values.global?.condoPaymentType === paymentType.Monthly && (
              <FormField
                type="select"
                name="global.condoBillingDayOfMonth"
                label="Condo Billing Day"
                mode="dark"
                options={billingDaysOptions}
                xs={4}
              />
            )}
          </Grid>
        </Grid>

        {isPaymentMortgage && (
          <Grid container>
            <Grid container spacing={4}>
              <FormField
                name="primaryMortgageDetail.mortgageHolderName"
                type="value"
                label="Lender Name"
                mode="dark"
                xs={4}
                value={
                  primaryMortgageDetail.mortgageHolderName ? primaryMortgageDetail.mortgageHolderName : 'Not provided'
                }
              />
              <FormField
                name="primaryMortgageDetail.loanNumber"
                type="value"
                label="Loan Number"
                mode="dark"
                xs={4}
                value={primaryMortgageDetail.loanNumber ? primaryMortgageDetail.loanNumber : 'Not provided'}
              />
              <FormField
                name="primaryMortgageDetail.mortgageHolderAddress.address"
                type="value"
                label="Lender Address"
                xs={4}
                mode="dark"
                value={
                  primaryMortgageDetail.mortgageHolderAddress?.address
                    ? `${primaryMortgageDetail.mortgageHolderAddress.address}, ${primaryMortgageDetail.mortgageHolderAddress.city}, 
                      ${primaryMortgageDetail.mortgageHolderAddress.state}, ${primaryMortgageDetail.mortgageHolderAddress.zip}`
                    : 'Not provided'
                }
              />
              <Grid item xs={12}>
                <UpdateMortgageNotification
                  isMortgageAdded={!!primaryMortgageDetail.mortgageHolderName}
                  onNavigateToDetails={goToCondoDetailsTab}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        {(offerMegaDownPay || megaDownPayRequired) && (
          <MegaDownPayCard megaDownPayRequired={megaDownPayRequired} formikValue="global.condoMegaDownPay" />
        )}
      </Card>
    </Section>
  );
});

CondoSettings.propTypes = {
  paymentMethodOptions: PropTypes.array.isRequired,
  billingDaysOptions: PropTypes.array.isRequired,
  paymentTypeOptions: PropTypes.array.isRequired
};

export default withDatePicker(CondoSettings);
